import axios from 'axios';
import * as XLSX from 'xlsx';

// Use environment variable for API base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000',
  timeout: 10000,
});

export const customerAPI = {
  getAll: () => api.get('/api/customers'),
  getById: (id) => api.get(`/api/customers/${id}`),
  search: (query) => api.get(`/api/customers/search/${query}`),
  create: (data) => api.post('/api/customers', data),
  update: (id, data) => api.put(`/api/customers/${id}`, data),
  delete: (id) => api.delete(`/api/customers/${id}`),
  bulkCreate: (data) => api.post('/api/customers/bulk', data),
};

export const productAPI = {
  getAll: () => api.get('/api/products'),
  getById: (id) => api.get(`/api/products/${id}`),
  create: (data) => api.post('/api/products', data),
  update: (id, data) => api.put(`/api/products/${id}`, data),
  delete: (id) => api.delete(`/api/products/${id}`),
  bulkCreate: (data) => api.post('/api/products/bulk', data),
};

export const supplierAPI = {
  getAll: () => api.get('/api/suppliers'),
  getById: (id) => api.get(`/api/suppliers/${id}`),
  search: (query) => api.get(`/api/suppliers/search/${query}`),
  create: (data) => api.post('/api/suppliers', data),
  update: (id, data) => api.put(`/api/suppliers/${id}`, data),
  delete: (id) => api.delete(`/api/suppliers/${id}`),
  bulkCreate: (data) => api.post('/api/suppliers/bulk', data),
  // Payment/credit APIs
  recordPayment: (id, data) => api.post(`/api/suppliers/${id}/payments`, data),
  getPayments: (id) => api.get(`/api/suppliers/${id}/payments`),
  getCredit: (id) => api.get(`/api/suppliers/${id}/credit`),
};

export const invoiceAPI = {
  getAll: () => api.get('/api/invoices'),
  getById: (id) => api.get(`/api/invoices/${id}`),
  create: (data) => api.post('/api/invoices', data),
  bulkCreate: (data) => api.post('/api/invoices/bulk', data),
};

export const purchaseAPI = {
  getAll: () => api.get('/api/purchases'),
  getById: (id) => api.get(`/api/purchases/${id}`),
  search: (query) => api.get(`/api/purchases/search/${query}`),
  create: (data) => api.post('/api/purchases', data),
  delete: (id) => api.delete(`/api/purchases/${id}`),
  bulkCreate: (data) => api.post('/api/purchases/bulk', data),
};

export const reportAPI = {
  getGSTReport: (fromDate, toDate) =>
    api.get('/api/reports/gst-report', { params: { fromDate, toDate } }),
  getNonGSTReport: (fromDate, toDate) =>
    api.get('/api/reports/non-gst-report', { params: { fromDate, toDate } }),
  getClosingStock: (threshold) =>
    api.get('/api/reports/closing-stock', { params: { threshold } }),
};

export const stockSettingsAPI = {
  getWarningThreshold: () => api.get('/api/settings/stock-warning-threshold'),
  setWarningThreshold: (value) => api.put('/api/settings/stock-warning-threshold', { value }),
};

export const userAPI = {
  getAll: () => api.get('/api/users'),
  getById: (id) => api.get(`/api/users/${id}`),
  create: (data) => api.post('/api/users', data),
  update: (id, data) => api.put(`/api/users/${id}`, data),
  delete: (id) => api.delete(`/api/users/${id}`),
  changePassword: (id, data) => api.post(`/api/users/${id}/change-password`, data),
};

// Utility function to parse CSV and Excel files
export const parseFileData = async (file) => {
  if (!file) throw new Error('No file provided');

  const isCSV = file.name.endsWith('.csv');
  const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

  if (!isCSV && !isExcel) {
    throw new Error('Please upload a CSV or Excel file');
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        if (isCSV) {
          // Parse CSV
          const text = e.target.result;
          const lines = text.split('\n').filter(line => line.trim());

          if (lines.length < 2) {
            throw new Error('File is empty or invalid');
          }

          const headers = lines[0].split(',').map(h => h.trim());
          const data = [];

          for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(',').map(v => v.trim());
            const row = {};
            headers.forEach((header, index) => {
              row[header] = values[index] || '';
            });
            data.push(row);
          }

          resolve(data);
        } else if (isExcel) {
          // Parse Excel
          const arrayBuffer = e.target.result;
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const data = XLSX.utils.sheet_to_json(worksheet);

          if (data.length === 0) {
            throw new Error('Excel file is empty');
          }

          resolve(data);
        }
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error('Error reading file'));

    if (isExcel) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  });
};

export default api;
